// src/components/FAQ.jsx
import { useNavigate } from 'react-router-dom';

function FAQ({ theme, toggleTheme }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2 font-medium"
          >
            ← Retour
          </button>
          <button
            onClick={toggleTheme}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-md font-medium"
          >
            {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
          </button>
        </header>

        {/* Titre principal */}
        <h1 className="text-4xl font-bold text-center mb-4 text-gray-900 dark:text-white">
          ❓ Foire aux questions (FAQ)
        </h1>
        <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
          Trouvez rapidement les réponses aux questions les plus fréquentes.
        </p>

        {/* Accordéons FAQ */}
        <div className="space-y-6">
          <details className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <summary className="px-6 py-5 font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition">
              Comment sont calculés les dividendes reçus ?
              <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
              <p>Le montant total reçu est calculé automatiquement :</p>
              <code className="mt-3 block font-mono text-sm bg-gray-100 dark:bg-gray-900 px-4 py-2 rounded-lg">
                dividend_per_share × nombre d'actions détenues à la date de détachement
              </code>
              <p className="mt-4">Vous n’avez rien à saisir manuellement.</p>
            </div>
          </details>

          <details className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <summary className="px-6 py-5 font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition">
              Mes données sont-elles sécurisées ?
              <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
              <p>Oui, absolument !</p>
              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Mots de passe hachés avec un algorithme fort</li>
                <li>Données stockées dans une base dédiée et isolée</li>
                <li>Accès restreint à votre compte uniquement</li>
                <li>Aucune donnée sensible visible en clair</li>
              </ul>
            </div>
          </details>

          <details className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <summary className="px-6 py-5 font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition">
              Puis-je importer mes données depuis mon broker ?
              <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
              <p>Pas encore en import direct, mais c’est dans les plans !</p>
              <p className="mt-3">
                En attendant, vous pouvez préparer vos données avec les{' '}
                <a href="/documentation" className="text-blue-600 dark:text-blue-400 underline hover:no-underline">
                  modèles CSV disponibles dans la documentation
                </a>.
              </p>
            </div>
          </details>

          <details className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <summary className="px-6 py-5 font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition">
              Que signifie PRU ?
              <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
              <p>
                <strong>Prix de Revient Unitaire</strong> : c’est le coût moyen d’achat d’une action, 
                en tenant compte de tous vos achats (et ventes éventuelles).
              </p>
              <p className="mt-2 text-sm">Il permet de calculer votre plus-value ou moins-value réelle.</p>
            </div>
          </details>

          <details className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <summary className="px-6 py-5 font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition">
              Comment changer le mois analysé dans le tableau de bord ?
              <span className="text-2xl text-gray-400 group-open:rotate-45 transition-transform">+</span>
            </summary>
            <div className="px-6 pb-6 pt-2 text-gray-600 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700">
              <p>Utilisez le sélecteur en haut à gauche du dashboard (format MM/AAAA).</p>
              <p className="mt-2">Toutes les données (valorisation, performance, dividendes...) s’ajustent automatiquement.</p>
            </div>
          </details>
        </div>

        {/* Pied de page */}
        <div className="text-center mt-16 text-gray-500 dark:text-gray-400">
          Une question non listée ? Contactez-nous directement !
        </div>
      </div>
    </div>
  );
}

export default FAQ;